import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, Button, Link, LinearProgress, Typography } from '../../ui/core';
import { useHasPermission } from '../../helpers/api/auth/index';
import { PERMISSIONS } from '../../helpers/api/auth/permissions';
import {
  claimKey,
  driverLabel,
  formatDateTime,
  formatPayoutAmount,
  formatProgress,
  payoutStatusLabel,
} from './claimFormatters';

// A Postgres-only driver (no Mongo id) has nothing for the profile route to
// resolve, and /driver-profile itself needs MANAGE_DRIVERS — without that
// check an admin without the permission would see a link that silently
// redirects to /home. Either way, plain text instead of a link.
const DriverCell = ({ row }) => {
  const canManageDrivers = useHasPermission(PERMISSIONS.MANAGE_DRIVERS);
  return row.driverMongoId && canManageDrivers ? (
    <Link to={`/driver-profile/${row.driverMongoId}`}>{driverLabel(row)}</Link>
  ) : (
    <Typography sx={{ fontSize: '13px' }}>{driverLabel(row)}</Typography>
  );
};

const DRIVER_COLUMN = { key: 'driver', label: 'Driver', render: (row) => <DriverCell row={row} /> };
const QUEST_COLUMN = { key: 'quest', label: 'Quest', render: (row) => row.questTitle };

// Deadline is deliberately absent from the Active columns: the merged
// HITCH-5504 endpoint doesn't return one, so there's nothing to render.
export const ACTIVE_COLUMNS = [
  DRIVER_COLUMN,
  QUEST_COLUMN,
  { key: 'questType', label: 'Quest type', render: (row) => row.questType },
  { key: 'progress', label: 'Progress', render: formatProgress },
  { key: 'claimed', label: 'Claimed', render: (row) => formatDateTime(row.claimedAt) },
];

export const COMPLETED_COLUMNS = [
  DRIVER_COLUMN,
  QUEST_COLUMN,
  { key: 'completed', label: 'Completed', render: (row) => formatDateTime(row.completedAt) },
  { key: 'payoutAmount', label: 'Payout amount', render: (row) => formatPayoutAmount(row.payoutAmountCents) },
  {
    key: 'payoutStatus',
    label: 'Payout status',
    // A null payoutStatus ("No payout record") renders in the error colour —
    // this is the payout-gap bug the tab exists to surface, so it must not
    // read as a routine "Pending".
    render: (row) => (
      <Typography color={row.payoutStatus ? undefined : 'error'} sx={{ fontSize: '13px' }}>
        {payoutStatusLabel(row.payoutStatus)}
      </Typography>
    ),
  },
];

export const CANCELED_COLUMNS = [
  DRIVER_COLUMN,
  QUEST_COLUMN,
  { key: 'claimed', label: 'Claimed', render: (row) => formatDateTime(row.claimedAt) },
  { key: 'canceled', label: 'Canceled', render: (row) => formatDateTime(row.canceledAt) },
];

// One table for all 3 tabs — the column set is the only thing that varies.
// Read-only v1: no action column, no buttons besides "Load more".
const ClaimsTable = ({ columns, rows, loading, hasMore, loadingMore, onLoadMore, emptyText }) => (
  <Box>
    {loading && <LinearProgress sx={{ mb: '8px' }} />}

    {!loading && rows.length === 0 ? (
      <Typography color="text.secondary" sx={{ fontSize: '13px' }}>
        {emptyText}
      </Typography>
    ) : (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={claimKey(row)}>
                {columns.map((column) => (
                  <TableCell key={column.key}>{column.render(row)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}

    {/* Hidden, not just disabled, while `loading`: a first page or a refetch
        in flight has no cursor to load more of yet, and showing an enabled
        button that does nothing (or re-fetches the old page) reads as a
        broken control rather than a loading state. */}
    {hasMore && !loading && (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: '12px' }}>
        <Button size="small" variant="outlined" disabled={loadingMore} onClick={onLoadMore}>
          {loadingMore ? 'Loading…' : 'Load more'}
        </Button>
      </Box>
    )}
  </Box>
);

export default ClaimsTable;
